import { Box, Container, Link, Typography, useTheme } from "@mui/material";
import { NavigationAndFooterLayout } from "../layouts";
import { NextPageWithLayout, YoutubeEmbed } from "@cm-websites/common";
import { NextSeo } from "next-seo";
import Image from "next/image";
import NextLink from "next/link";
import React from "react";
import AufmacherBild from "../../public/images/wie-funktioniert-ein-krimidinner.jpg";
import YoutubePoster from "../../public/images/youtube-poster-3.jpg";

export const WasIstDasPage: NextPageWithLayout = () => {
  const theme = useTheme();

  return (
    <React.Fragment>
      <NextSeo
        description="So funktioniert ein Krimidinner für Zuhause. Erfahre in unserem Video alles, was du zum Thema Krimdinner wissen musst."
        title="Wie funktioniert ein Krimidinner für Zuhause?"
      />
      <Container maxWidth="md" sx={{ pt: { xs: 12, sm: 14, lg: 16 }, pb: 4 }}>
        <Typography variant="h1" sx={{ pb: 6, hyphens: "none" }}>
          Wie funktioniert ein Krimidinner für Zuhause?
        </Typography>
        <Typography variant="body1" paragraph>
          Du und deine Gäste schlüpfen in die Rollen der Hauptverdächtigen eines spannenden
          Kriminalfalls. Das Ziel des Spiels ist es, den Täter, der mit am Tisch sitzt, zu überführen.
          Dies kann nur gelingen, wenn die Spieler untereinander den Tatablauf diskutieren und ihre
          Erinnerungen an das Geschehen zusammentragen. Doch Vorsicht: Jede Figur (dich mit
          eingeschlossen!) hat ihre eigenen dunklen Geheimnisse, die sie am liebsten für sich behalten
          würde.
        </Typography>
      </Container>
      <Container maxWidth="lg" sx={{ pt: 0, pb: 4 }}>
        <Box>
          <YoutubeEmbed
            aspectRatio="16 / 9"
            id="7JPLYQ4zF_s"
            posterImage={YoutubePoster}
            title="Wie funktioniert ein Krimidinner für Zuhause?"
          />
        </Box>
      </Container>

      <Container maxWidth="md" sx={{ pt: 4, pb: { xs: 8, sm: 10, lg: 14, xl: 16 } }}>
        <Typography component="h2" variant="h2" paragraph>
          Ablauf
        </Typography>
        <Typography variant="body1" paragraph>
          Bei einem Krimidinner für Zuhause sitzt ihr nach der Tat zusammen und versucht, eure
          Erinnerungen zusammenzutragen, um dem Täter auf die Schliche zu kommen. Dabei ruft ihr euch mit
          jeder Spielrunde weitere Erinnerungen an den Abend und den Tathergang ins Gedächtnis, denn jede
          Runde behandelt einen anderen Zeitabschnitt des Tatabends. Zum Schluss wird dann gemeinsam
          darüber abgestimmt, wer als Tatverdächtiger der Polizei übergeben werden soll.
        </Typography>

        <Typography variant="body1" paragraph>
          Culinario Mortale ist ein sehr interaktives Spiel. Es gibt nur wenige Spielregeln und keine
          vorgefertigten Dialoge – das gemeinsame Knobeln und Diskutieren steht im Vordergrund.
        </Typography>

        <Box sx={{ my: 6 }}>
          <Image
            alt="Krimidinner Spiel vor einem Kochtopf mit Gewürzen und Zutaten"
            placeholder="blur"
            src={AufmacherBild}
            title="Serviere einen Mord zum Dinner!"
            style={{
              borderRadius: theme.shape.borderRadius,
              width: "100%",
              height: "auto",
              overflow: "hidden",
            }}
          />
        </Box>

        <Typography component="h3" variant="h2" paragraph>
          Vorbereitung
        </Typography>
        <Typography variant="body1" paragraph>
          Neben einer klar strukturierten Spielanleitung enthält jeder Fall Spielhefte für fünf bis acht
          Figuren{" "}
          <em>
            (die jeweilige Spieleranzahl findest du jeweils auf der Verpackung und hier auf unserer
            Internetseite)
          </em>
          . In diesen Spielheften findet jeder Gast seine persönliche Hinweise und Rollenbeschreibung.
          Außerdem beinhaltet jedes Spielheft eine kurze{" "}
          <NextLink href="/spielregeln-und-ablauf">Übersicht</NextLink>, wie das Spiel funktioniert.
          Somit können alle Spieler direkt loslegen. Mit deinem persönlichen Support-Code, der jedem
          Culinario Mortale Spiel beiliegt, kannst du die Rollenbeschreibungen im Vorfeld deines
          Krimidinners auf unserer Seite als PDF-Datei{" "}
          <NextLink href="/vorbereitung">herunterladen</NextLink> und deinen Gästen ganz bequem per
          E-Mail schicken.
        </Typography>

        <Typography variant="body1" paragraph>
          Solltest du Fragen haben, zögere bitte nicht, uns zu kontaktieren. Wir freuen uns immer über
          Lob, Kritik und Anregungen. Schicke dein Feedback oder deine Frage per E-Mail an:{" "}
          <Link href="mailto:support@culinario-mortale.de">support @ culinario-mortale.de</Link>
        </Typography>
      </Container>
    </React.Fragment>
  );
};

WasIstDasPage.getLayout = (page) => <NavigationAndFooterLayout>{page}</NavigationAndFooterLayout>;

export default WasIstDasPage;
